import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { rem } from 'polished'
import styled from 'styled-components'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import {
  formatDate,
} from 'components/SearchV2/Utils/GlobalSearchV2'
import DateFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/DateFloatingPanel'
import LocationFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/LocationFloatingPanel'
import useDateSearchProps from 'components/SearchV2/Hooks/useDateSearchProps'
import useStepsController from 'components/SearchV2/Hooks/useStepsController'
import { DATE_SEARCH_OPTION_IDS, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import { BusinessTravellerMultiRoomHotelsSelectMobile, BusinessTravellerMultiRoomHotelsSelectDisabled } from 'businessTraveller/components/select-traveller/multi-room-hotels'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'
import useRecentSearches from 'hooks/Search/useRecentSearches'
import { EmptyArray } from 'lib/array/arrayUtils'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    &:not(:first-child) {
      margin-top: ${rem(8)};
    }
  }
`

export enum searchSteps {
  LOCATION = 'location',
  DATE = 'date',
  GUESTS = 'guests',
  MULTI_ROOMS = 'multiRooms',
}

interface Props {
  closeSearch?: () => void
  onSearch: () => void
  isInPage?: boolean; // i.e. not part of drawer
}

function HotelsTabContent(props: Props) {
  const { onSearch } = props

  const {
    searchItem,
    checkinDate,
    checkoutDate,
    occupancies = EmptyArray,
    flexibleMonths,
    flexibleNights,
    dateSearchOptionId,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const { openedSearchVertical, option } = useGlobalSearchURLHashVertical()

  const isDateSelectOpen = openedSearchVertical === 'hotels' && option === 'dateSelect'

  const recentSearches = useRecentSearches()

  const stepsValidation = useMemo(
    () => ({
      [searchSteps.LOCATION]: searchItem,
      [searchSteps.DATE]: checkinDate && checkoutDate,
      [searchSteps.GUESTS]: occupancies.length > 0,
      [searchSteps.MULTI_ROOMS]: occupancies.length > 0,
    }),
    [searchItem, checkinDate, checkoutDate, occupancies.length],
  )

  const flexibleSearchSelectionText =
    prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights)

  const [activeStep, setActiveStep] = useState<searchSteps>()
  const { goToNextStep, goToPreviousStep, closeActiveStep, isSearchEnabled } =
    useStepsController({ activeStep, setActiveStep, stepsValidation, onSearch })

  const openLocationSearchStep = useCallback(
    () => setActiveStep(searchSteps.LOCATION),
    [setActiveStep],
  )
  const openDateSearchStep = useCallback(
    () => setActiveStep(searchSteps.DATE),
    [setActiveStep],
  )
  const openGuestsSearchStep = useCallback(
    () => setActiveStep(searchSteps.GUESTS),
    [setActiveStep],
  )

  const openMultiRoomsSearchStep = useCallback(
    () => setActiveStep(searchSteps.MULTI_ROOMS),
    [setActiveStep],
  )

  const goToNextMissingStep = useCallback(() => {
    if (!stepsValidation[searchSteps.LOCATION]) {
      openLocationSearchStep()
    } else if (!stepsValidation[searchSteps.DATE]) {
      openDateSearchStep()
    } else {
      closeActiveStep()
    }
  }, [stepsValidation, openLocationSearchStep, openDateSearchStep, closeActiveStep])

  useEffect(() => {
    if (isDateSelectOpen) {
      openDateSearchStep()
    }
  }, [isDateSelectOpen, openDateSearchStep])

  const { checkin, checkout } = useMemo(
    () => ({
      checkin: formatDate(checkinDate),
      checkout: formatDate(checkoutDate),
    }),
    [checkinDate, checkoutDate],
  )

  const dateSearchProps = useDateSearchProps({
    onAnytimeDatesSelected: closeActiveStep,
  })

  return (
    <ContentWrapper>
      <LocationFloatingPanel
        typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
        isOpen={activeStep === searchSteps.LOCATION}
        onClose={closeActiveStep}
        onConfirm={goToNextMissingStep}
        onGoBack={goToPreviousStep}
        typeaheadVertical="hotel"
        recentSearches={recentSearches}
      />

      <DateFloatingPanel
        {...dateSearchProps}
        isOpen={activeStep === searchSteps.DATE}
        onClose={closeActiveStep}
        onConfirm={goToNextStep}
        onGoBack={goToPreviousStep}
        confirmText={isSearchEnabled ? 'Search' : 'Apply'}
      />

      <FieldsWrapper>
        <BusinessTravellerAccountGuard
          employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
          loading={<BusinessTravellerSelectLoadingSkeleton />}
          fallback={<BusinessTravellerMultiRoomHotelsSelectDisabled />}
        >
          <BusinessTravellerMultiRoomHotelsSelectMobile
            activeStep={activeStep}
            openEmployeeMenu={openGuestsSearchStep}
            openRoomMenu={openMultiRoomsSearchStep}
            onSelectionApply={goToNextMissingStep}
          />
        </BusinessTravellerAccountGuard>

        <SearchFormField
          label="Where"
          placeholder="Search destination, landmark or hotel"
          value={searchItem?.format.mainText}
          onClick={openLocationSearchStep}
          required
          requiredErrorMessage="Please select a destination"
        />

        <SearchFormFieldGroup>
          {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <SearchFormField
              label="When"
              onClick={openDateSearchStep}
              placeholder="Anytime"
              value={flexibleSearchSelectionText}
            />
          )}
          {dateSearchOptionId !== DATE_SEARCH_OPTION_IDS.FLEXIBLE && (
            <>
              <SearchFormField
                label="Check in"
                placeholder="Select date"
                value={checkin}
                onClick={openDateSearchStep}
                required
                requiredErrorMessage="Please select check-in date"
              />
              <SearchFormField
                label="Check out"
                placeholder="Select date"
                value={checkout}
                onClick={openDateSearchStep}
                required
                requiredErrorMessage="Please select check-out date"
              />
            </>
          )}
        </SearchFormFieldGroup>
      </FieldsWrapper>
    </ContentWrapper>
  )
}

export default HotelsTabContent
